import { AdasBox, AdasTypography } from '@components/wrapper-components'

const ALLSTATE_CALIBRATION_LEVELS = [
  {
    levels: [
      'Level 3a: Targeted Calibrations (All Other Makes)*',
      'Level 3b: Targeted Calibrations (Mercedes, Subaru)**',
      'Level 3c: Targeted Calibrations (Porsche, Audi, VW)***'
    ]
  }
]
const CSAA_CALIBRATION_LEVELS = [
  {
    title: 'Radar Sensor Calibration (RS) – OEM-Based',
    levels: ['RS1: Audi, BMW, Maserati, Nissan, Porsche, Subaru, VW', 'RS2: All other OEMs']
  },
  {
    title: 'Surround View Camera Calibration (SV) – OEM-Based',
    levels: ['SV1: Audi, BMW, Ford, Maserati, Nissan, Porsche, Toyota, VW', 'SV2: All other OEMs']
  }
]
const PROGRESSIVE_CALIBRATION_LEVELS = [
  {
    levels: ['Level 3a: Domestic, Honda, Toyota, Nissan', 'Level 3b: Other Asian (Hyundai/Kia, Subaru, Mazda)', 'Level 3c: European']
  }
]
const ERAC_CALIBRATION_LEVELS = [
  {
    levels: ['Level 3a: Domestic and Asian', 'Level 3c: European', 'Level 3d: Dual (Static + Dynamic)']
  }
]
// Carriers with an empty level list render the title bar only (no level breakdown).
const TITLE_BAR_ONLY = []

// Single source of truth per national-contract carrier, keyed by referral source `code`
// (which equals QWIData.referralSourceCode). `name` is the canonical label shown in the
// banner header — never taken from the referral API string, so the display always
// matches the locked design regardless of what the API returns.
const CARRIER_CONFIG = {
  AS: { name: 'Allstate', levels: ALLSTATE_CALIBRATION_LEVELS },
  SF: { name: 'State Farm', levels: ALLSTATE_CALIBRATION_LEVELS },
  CSAA: { name: 'CSAA', levels: CSAA_CALIBRATION_LEVELS },
  BRI: { name: 'Bear River', levels: TITLE_BAR_ONLY },
  HANOVER: { name: 'Hanover', levels: TITLE_BAR_ONLY },
  HAN: { name: 'Hanover', levels: TITLE_BAR_ONLY },
  PROGRESSIVE: { name: 'Progressive', levels: PROGRESSIVE_CALIBRATION_LEVELS },
  ERAC: { name: 'Enterprise (ERAC)', levels: ERAC_CALIBRATION_LEVELS },
  NODAK: { name: 'Nodak Insurance', levels: PROGRESSIVE_CALIBRATION_LEVELS }
}

// Alternate referral signals (company_abbr, normalized company display name) → config code,
// so matching still works if the `code` field is ever missing.
const CARRIER_ALIASES = {
  allstate: 'AS',
  state_farm: 'SF',
  'state farm': 'SF',
  csaa_insurance: 'CSAA',
  'csaa insurance': 'CSAA',
  bear_river_insurance: 'BRI',
  'bear river insurance': 'BRI',
  'bear river': 'BRI',
  hanover: 'HANOVER',
  'the hanover insurance group': 'HANOVER',
  progressive: 'PROGRESSIVE',
  'progressive insurance': 'PROGRESSIVE',
  enterprise: 'ERAC',
  'enterprise rent-a-car': 'ERAC',
  nodak: 'NODAK',
  'nodak insurance': 'NODAK'
}

// Resolves a referral to its carrier config, trying the most reliable key first.
const resolveCarrier = (referral) => {
  const code = referral?.code?.trim().toUpperCase()
  if (code && CARRIER_CONFIG[code]) return CARRIER_CONFIG[code]

  const abbr = referral?.company_abbr?.trim().toLowerCase()
  if (abbr && CARRIER_ALIASES[abbr]) return CARRIER_CONFIG[CARRIER_ALIASES[abbr]]

  const company = referral?.company?.trim().toLowerCase().replace(/\s+/g, ' ')
  if (company && CARRIER_ALIASES[company]) return CARRIER_CONFIG[CARRIER_ALIASES[company]]

  return undefined
}

// Splits a level string like "Level 3a: Targeted Calibrations (All Other Makes)*"
// into its badge code ("3a"), body text, and trailing asterisk marker ("*").
const parseLevel = (raw) => {
  const separatorIndex = raw.indexOf(':')
  let code = ''
  let rest = raw
  if (separatorIndex !== -1) {
    code = raw
      .slice(0, separatorIndex)
      .replace(/^Level\s+/i, '')
      .trim()
    rest = raw.slice(separatorIndex + 1).trim()
  }
  const starMatch = rest.match(/(\*+)\s*$/)
  const stars = starMatch ? starMatch[1] : ''
  const text = stars ? rest.slice(0, rest.length - stars.length).trim() : rest
  return { code, text, stars }
}

// True when ProductContractBanner renders for this referral. Referral codes with no carrier
// config (e.g. GEICO, FARMERS) get no banner, so callers can show a fallback instead.
export const hasContractBanner = (referralData) => !!resolveCarrier(referralData)

export const ProductContractBanner = ({ referralData }) => {
  const carrier = resolveCarrier(referralData)
  if (!carrier) return null
  const calibrationLevels = carrier.levels
  const shouldShowLevels = calibrationLevels.length > 0

  return (
    <AdasBox className='overflow-hidden mt-2 mb-4 rounded-[12px] border border-[#0A1628]/10 shadow-sm'>
      {/* Header — navy gradient title bar with inline eyebrow - carrier name */}
      <AdasBox
        className='flex items-center gap-3.5 w-full py-2.5 px-5 text-[#fff]'
        style={{ background: 'linear-gradient(135deg, #172641 0%, #2B4064 100%)' }}
      >
        {/* Title — eyebrow - carrier name inline, matching type styles */}
        <AdasBox className='flex items-center flex-wrap gap-x-2 gap-y-0.5 flex-1 min-w-0'>
          <AdasTypography
            variant='caption'
            fontWeight='700'
            className='uppercase leading-tight text-[13px] tracking-[0.08em] text-[#9FB2D0]'
          >
            National Contract Pricing
          </AdasTypography>
          <AdasTypography variant='caption' fontWeight='700' className='uppercase leading-tight text-[13px] tracking-[0.08em] text-[#fff]'>
            - {carrier.name}
          </AdasTypography>
        </AdasBox>
      </AdasBox>

      {/* Carrier-specific calibration levels */}
      {shouldShowLevels && (
        <AdasBox className='flex flex-col bg-[#fff]'>
          {calibrationLevels.map((section, sectionIndex) => (
            <AdasBox key={section.title || sectionIndex}>
              {section.title && (
                <AdasTypography
                  variant='caption'
                  fontWeight='600'
                  className='block uppercase pt-4 px-5 text-[11px] tracking-[0.06em] text-[#6B7A94]'
                >
                  {section.title}
                </AdasTypography>
              )}
              {section.levels.map((subLevel) => {
                const { code, text, stars } = parseLevel(subLevel)
                return (
                  <AdasBox key={code || text} className='flex items-baseline gap-3 py-1.5 px-5'>
                    <AdasTypography variant='subtitle2' className='shrink-0 text-[14px] font-normal text-[#2B3E63]'>
                      {code}
                    </AdasTypography>
                    <AdasTypography variant='subtitle2' className='text-[14px] font-normal text-[#2E3D57]'>
                      {text}
                      {stars && <span style={{ color: '#9AA7BC' }}>{stars}</span>}
                    </AdasTypography>
                  </AdasBox>
                )
              })}
            </AdasBox>
          ))}
        </AdasBox>
      )}
    </AdasBox>
  )
}
